import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FormControlLabel, Checkbox } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { EditableDataGrid, createColumns } from '../components/EditableDataGrid';
import { StageProcessor } from '../components/StageProcessor';
import { findMatchingProducts, getStageProgress, resetStageProgress } from '../api/stageFunctions';
import { MFR, Product } from '../types/pipeline';
import { useStageCache } from '../hooks/useStageCache';
import { STAGE_NUMBERS } from '../utils/cacheUtils';
import { getButtonText } from '../utils/cacheUIHelpers';

interface EnhancedProductDiscoveryProps {
  mfr: MFR;
  onComplete: (products: Product[]) => void;
  flowId?: string | null;
}

const EnhancedProductDiscovery: React.FC<EnhancedProductDiscoveryProps> = ({
  mfr,
  onComplete,
  flowId,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [triggerSearch, setTriggerSearch] = useState(0);
  const [specsDialogOpen, setSpecsDialogOpen] = useState(false);
  const [selectedSpecs, setSelectedSpecs] = useState<string[]>([]);
  const [useCachedData, setUseCachedData] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [progressMessages, setProgressMessages] = useState<{ timestamp: string; message: string }[]>([]);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Check cache for this stage
  const { cachedData, isCacheValid, isCheckingCache, refreshCache, checkCacheManually, validateCacheWithInput } = useStageCache({
    flowId,
    stageNumber: STAGE_NUMBERS.PRODUCTS,
    inputData: mfr,
    enabled: useCachedData,
  });

  // Auto-search on mount ONLY if no cached data, and initialize MFR text
  // Validate cache only once per distinct input (flowId + requirements text)
  const lastValidatedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!flowId || !useCachedData) return;
    const reqText = mfr.rawText || mfr.commodityDescription || '';
    const key = `${flowId}::${reqText}`;
    if (lastValidatedKeyRef.current !== key) {
      lastValidatedKeyRef.current = key;
      validateCacheWithInput();
    }
  }, [flowId, useCachedData, mfr.rawText, mfr.commodityDescription, validateCacheWithInput]);

  // Default checkbox to match cache availability (disabled when no cache)
  useEffect(() => {
    if (!isCheckingCache) {
      setUseCachedData(isCacheValid);
    }
  }, [isCheckingCache, isCacheValid]);

  // Auto-search on mount ONLY to let StageProcessor run (cache or compute)
  // Require a valid flowId so backend can persist progress for polling
  useEffect(() => {
    if (!isCheckingCache && !hasInitialized && flowId) {
      setHasInitialized(true);
      handleSearchProducts();
    }
  }, [isCheckingCache, hasInitialized, flowId]);

  // Cleanup progress timer and SSE on unmount
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current as any);
        progressTimerRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const handleSearchProducts = async () => {
    // Guard: need a valid flowId to persist and fetch progress
    if (!flowId) {
      console.warn('EnhancedProductDiscovery: No flowId set yet; delaying search.');
      return;
    }

    setIsSearching(true);
    setProgressMessages([]);

    // Clear any existing timers or SSE connections
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current as any);
      progressTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      try {
        eventSourceRef.current.close();
      } catch {}
      eventSourceRef.current = null;
    }

    // Ask backend to wipe persisted progress so SSE replay starts clean
    try {
      await resetStageProgress(flowId, 3);
    } catch (error) {
      console.warn('EnhancedProductDiscovery: Failed to reset progress before search', error);
    }

    // Try SSE first
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const userEmail = localStorage.getItem('userEmail') || window.userEmail || '';
    try {
      const params = new URLSearchParams();
      params.set('skip_history', 'true');
      if (userEmail) {
        params.set('email', userEmail);
      }
      const sseUrl = `${API_BASE_URL}/api/flows/${flowId}/stage/3/progress/stream?${params.toString()}`;
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;
      es.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          setProgressMessages((prev) => [...prev, payload]);
          if (payload.done) {
            es.close();
            eventSourceRef.current = null;
          }
        } catch {}
      };
      es.onerror = () => {
        // Fallback to polling if SSE errors
        try { es.close(); } catch {}
        eventSourceRef.current = null;
        if (!progressTimerRef.current) {
          progressTimerRef.current = setInterval(async () => {
            try {
              const progress = await getStageProgress(flowId, 3);
              if (progress) {
                setProgressMessages(progress.messages || []);
                if (progress.done) {
                  if (progressTimerRef.current) clearInterval(progressTimerRef.current as any);
                  progressTimerRef.current = null;
                }
              }
            } catch (e) {
              // ignore polling errors
            }
          }, 800) as any;
        }
      };
    } catch (e) {
      // ignore SSE init errors; polling fallback below
    }

    // Start polling progress if SSE is unavailable
    if (!eventSourceRef.current) {
      progressTimerRef.current = setInterval(async () => {
        try {
          const progress = await getStageProgress(flowId, 3);
          if (progress) {
            setProgressMessages(progress.messages || []);
            if (progress.done) {
              if (progressTimerRef.current) clearInterval(progressTimerRef.current as any);
              progressTimerRef.current = null;
            }
          }
        } catch (e) {
          // ignore polling errors
        }
      }, 800) as any;
    }

    setTriggerSearch(prev => prev + 1);
  };
  
  // Load cached data when it becomes available
  useEffect(() => {
    if (isCacheValid && cachedData && useCachedData && products.length === 0) {
      // Only load if we don't already have products
      handleProductsFound(cachedData);
    }
  }, [isCacheValid, cachedData, useCachedData, products.length]);

  const handleProductsFound = (data: any) => {
    console.log('handleProductsFound called with:', data);
    // With UI-ready format, products are always in a consistent format
    const products = data.products || data;
    
    if (!Array.isArray(products)) {
      console.error('EnhancedProductDiscovery: Expected products array but got:', data);
      return;
    }
    
    console.log('Setting products:', products);
    // Ensure each product has a stable unique id for MUI DataGrid
    const withIds = products.map((p: any, idx: number) => {
      const slug = (s: string) =>
        s
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

      const fallbackName = (p.product_name || p.name || p.title || 'Unnamed Product').toString();
      const normalizedId = (p.id || `${slug(fallbackName)}-${slug(p.manufacturer || '')}-${slug(p.model || '')}-${idx}` || `prod-${idx}`).toString();

      return {
        ...p,
        id: normalizedId,
        name: p.name || fallbackName,
        product_name: p.product_name || fallbackName,
        manufacturer: p.manufacturer || p.vendor || p.brand || '',
        model: p.model || p.model_number || p.item || '',
      };
    });
    setProducts(withIds);
    // Stop progress polling when results arrive
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current as any);
      progressTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      try { eventSourceRef.current.close(); } catch {}
      eventSourceRef.current = null;
    }
    setIsSearching(false);

    // If the run completed too quickly (e.g., cache hit), polling may not have
    // picked up the progress messages yet. Fetch once immediately so the
    // "Discovery Progress" panel shows cached progress lines reliably.
    if (flowId) {
      (async () => {
        try {
          const progress = await getStageProgress(flowId!, 3);
          if (progress && Array.isArray(progress.messages) && progress.messages.length > 0) {
            setProgressMessages(progress.messages);
          }
        } catch (_) {
          // ignore
        }
      })();
    }
    
    // Auto-select all products since they've already passed relevance filtering
    const autoSelected = new Set(withIds.map(p => p.id));
    setSelectedProducts(autoSelected);
    console.log('Products set successfully, count:', products.length);
  };

  const handleProductSelection = (productId: string, selected: boolean) => {
    const newSelection = new Set(selectedProducts);
    if (selected) {
      newSelection.add(productId);
    } else {
      newSelection.delete(productId);
    }
    setSelectedProducts(newSelection);
  };

  const handleViewSpecs = (specs: string[]) => {
    setSelectedSpecs(specs);
    setSpecsDialogOpen(true);
  };

  const handleComplete = () => {
    const selected = products.filter(p => selectedProducts.has(p.id));
    if (selected.length === 0) {
      alert('Please select at least one product to continue');
      return;
    }
    onComplete(selected);
  };

  // Prepare data for grid with selection state
  const prepareGridData = () => {
    return products.map(p => ({
      ...p,
      selected: selectedProducts.has(p.id),
    }));
  };

  // Define columns for products grid
  const productColumns = [
    {
      field: 'selected',
      headerName: 'Select',
      width: 80,
      renderCell: (params: any) => (
        <input
          type="checkbox"
          checked={params.value}
          onChange={(e) => handleProductSelection(params.row.id, e.target.checked)}
          style={{ width: 20, height: 20, cursor: 'pointer' }}
        />
      ),
    },
    createColumns.text('name', 'Product Name', 350),
    createColumns.text('manufacturer', 'Manufacturer', 250),
    createColumns.text('model', 'Model', 200),
    {
      field: 'source',
      headerName: 'Source',
      width: 200,
      renderCell: (params: any) => {
        const product = params.row as Product;
        const rawSource = (product.evidence?.source || 'unknown').toString();
        const src = rawSource.toLowerCase();
        const url = product.evidence?.url;
        const mandatorySources = new Set([
          'fpds',
          'fpds_atom',
          'usaspending',
          'gsa',
          'sam',
          'sam_gov',
          'beta.sam',
          'gov',
          'catalog:',
          'mandatory_catalog',
        ]);
        const sourceType = (product.source_type || '').toLowerCase();
        const isMandatory =
          sourceType.includes('mandatory') ||
          Array.from(mandatorySources).some(key => src.includes(key));
        const label = isMandatory ? 'Mandatory' : 'Open Market';
        const color = isMandatory ? 'success' : 'warning';
        const catalogUrl = product.evidence?.catalog_url;
        const catalogId = product.evidence?.catalog_id;
        // Use API endpoint for catalog PDFs
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const catalogApiUrl = catalogId ? `${apiBaseUrl}/api/catalogs/${catalogId}` : null;
        const primaryUrl = catalogApiUrl || catalogUrl || url;
        const displayText = catalogId || rawSource;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip size="small" label={label} color={color as any} variant="outlined" />
            {primaryUrl ? (
              <a href={primaryUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                <Typography variant="caption" color="primary">{displayText}</Typography>
              </a>
            ) : (
              <Typography variant="caption" color="text.secondary">{displayText}</Typography>
            )}
          </Box>
        );
      },
    },
  ];

  // Calculate statistics
  const stats = {
    total: products.length,
    selected: selectedProducts.size,
    gsa: products.filter(p => p.source === 'GSA').length,
  };

  return (
    <Box>
      {/* Active Flow Banner */}
      {flowId && (
        <Paper sx={{ p: 1, mb: 1, backgroundColor: '#fffbe6', border: '1px dashed #f0c36d' }}>
          <Typography variant="caption">Active Flow: {flowId}</Typography>
        </Paper>
      )}
      {/* Live Progress (multi-line) */}
      {flowId && (isSearching || progressMessages.length > 0) && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f9fbff' }}>
          <Typography variant="subtitle2" gutterBottom>Discovery Progress</Typography>
          {progressMessages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Waiting for updates…</Typography>
          ) : (
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
              {progressMessages.map((m, i) => (
                <div key={i}>[{new Date(m.timestamp).toLocaleTimeString()}] {m.message}</div>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* Cache Control and Search Button */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={useCachedData}
                onChange={(e) => setUseCachedData(e.target.checked)}
                color="primary"
                disabled={isCheckingCache || !isCacheValid}
              />
            }
            label="Use cached data when available"
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SearchIcon />}
            onClick={handleSearchProducts}
            disabled={isCheckingCache || !flowId}
          >
            {getButtonText('Search Products', useCachedData, cachedData && cachedData.products)}
          </Button>
        </Box>
      </Paper>
      
      {/* Search Products */}
      <StageProcessor
        title="Product Discovery"
        description="Searching for products that match your requirements..."
        processFunction={() => findMatchingProducts(mfr, flowId, !useCachedData)}
        onSuccess={handleProductsFound}
        trigger={triggerSearch}
        autoProcess={false}
        showStatus={true}
      >
        {(data, status) => {
          // Use products state instead of data from StageProcessor
          if (products.length === 0) return null;

          return (
            <>
              {/* Statistics */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="h6">
                    <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
                    Product Search Results
                  </Typography>
                  <Chip 
                    label={`${stats.total} Products Found`} 
                    color="primary" 
                    variant="outlined"
                  />
                  <Chip 
                    label={`${stats.selected} Selected`} 
                    color="success"
                    variant={stats.selected > 0 ? "filled" : "outlined"}
                  />
                </Box>
              </Paper>


              {/* Products Grid */}
              <Paper sx={{ p: 2 }}>
                <EditableDataGrid
                  data={prepareGridData()}
                  columns={productColumns}
                  onDataChange={(updatedData) => {
                    // Update products but maintain selection separately
                    const updatedProducts = updatedData.map(({ selected, ...product }) => product as Product);
                    setProducts(updatedProducts);
                  }}
                  onAddRow={() => {
                    const newProduct: any = {
                      id: `custom-${Date.now()}`,
                      name: 'New Custom Product',
                      manufacturer: 'Custom',
                      model: '',
                      specifications: [],
                      price: '0',
                      gsaSchedule: false,
                      contractVehicle: 'Open Market',
                      leadTime: 30,
                      matchScore: 50,
                      selected: false,
                    };
                    return newProduct;
                  }}
                  height={500}
                  showToolbar={true}
                  selectable={false}
                  customActions={
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleSearchProducts}
                    >
                      Search Again
                    </Button>
                  }
                />

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Select products to include in PSC code assignment
                  </Typography>
                  <Box>
                    {/* JSON export removed for non-technical users */}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleComplete}
                      disabled={selectedProducts.size === 0}
                    >
                      Continue to PSC Codes ({selectedProducts.size} products)
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          );
        }}
      </StageProcessor>

      {/* Specs Dialog */}
      <Dialog
        open={specsDialogOpen}
        onClose={() => setSpecsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Key Specifications</DialogTitle>
        <DialogContent>
          <List>
            {selectedSpecs.map((spec, index) => (
              <ListItem key={index}>
                <ListItemText primary={`• ${spec}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EnhancedProductDiscovery;
